import { Badge, EyeOpenIcon, Icon, Pane, Popover, Stack, UserIcon } from 'evergreen-ui';
import moment from 'moment';
import { Link } from 'react-router-dom';

import { daysLabel, daysSince } from '../../../v2/features/threads/lib/daysSince';
import {
    AlertBlock,
    BackorderBlock,
    CompanyBlock,
    ExternalBlock,
    PaymentBlock,
    PreorderBlock,
    ReviewBlock,
    SinglesBlock,
    StatusBlock,
    StorePickupBlock,
    TypeBlock,
} from './ComponentBlocks';
import OrderPreview from './OrderPreview';

// Single source of truth for order table columns. All three displays (the
// Orders-to-Process widget, the Tickete widget, the order listing) compose
// their column array from getOrderColumns(...) so the rows render identically.
// Row shape differs by call site — the order listing maps through the Order
// structure (moment dates, *Text labels), the dashboard widgets pass plain API
// rows — so the decorators tolerate both via the *Label fallbacks below.

const statusLabels = {
    placed: 'Plasata',
    confirmed: 'Confirmata',
    processed: 'Procesata',
    delivered: 'Livrata',
    completed: 'Finalizata',
    cancelled: 'Anulata',
    returning: 'Retur in curs',
    returned: 'Returnata',
};

const paymentLabels = {
    unpaid: 'Neplatita',
    partial_payment: 'Plata Partiala',
    paid: 'Platita',
    payment_failed: 'Plata Esuata',
    refunded: 'Returnata',
};

const typeLabels = {
    facebook: 'Facebook',
    glovo: 'Glovo',
    online: 'Online',
    physical: 'Fizic',
    phone: 'Telefonic',
    email: 'Email',
};

const deliveryLabels = {
    delivery: 'Curier',
    'delivery-locker': 'Easybox',
    personal: 'Ridicare PL',
    'personal-brasov': 'Ridicare BV',
};

const labelOf = (map, value) => map[value] || value || '-';

const datetimeCell = (value) => {
    if (!value) return '-';
    const m = moment.isMoment(value) ? value : moment(value);
    return m.isValid() ? m.format("D MMM 'YY HH:mm") : '-';
};

const issueButtonStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 22,
    height: 22,
    borderRadius: 4,
    background: '#facc15',
    color: '#000',
    cursor: 'pointer',
    border: 'none',
    padding: 0,
    fontWeight: 700,
    fontSize: 14,
    lineHeight: '22px',
};

/**
 * @param {(target: {targetType, targetId, label}) => void} [onOpenThreads]
 *        opens the order's tickets modal when the "!" badge is clicked.
 * @param {boolean} [withPreview] render the hover item-preview eye next to the
 *        order number (order listing only — widget rows carry no item titles).
 */
export function getOrderColumns({ onOpenThreads, withPreview = false } = {}) {
    return {
        // The yellow "!" opens the order's tickets. Renders only for orders
        // that carry an open ticket (issues > 0 — ticket-maintained); for
        // everyone else the cell is empty so the column reads as "needs attention".
        issue: {
            id: 'issue',
            filterId: 'issues',
            label: '',
            flexBasis: 48,
            decorator: (row) => {
                if (!row.issues || row.issues <= 0) return null;
                const days = daysSince(row.oldestIssueAt);
                return (
                    <Pane
                        is="button"
                        type="button"
                        style={issueButtonStyle}
                        aria-label={`Vezi ticketele comenzii #${row.orderNumber}${days > 0 ? ` (nerezolvate de ${daysLabel(days)})` : ''}`}
                        title={days > 0 ? `Nerezolvate de ${daysLabel(days)}` : 'Vezi ticketele'}
                        onClick={() =>
                            onOpenThreads &&
                            onOpenThreads({
                                targetType: 'order',
                                targetId: row.id,
                                label: `Comanda #${row.orderNumber}`,
                            })
                        }
                    >
                        !
                    </Pane>
                );
            },
        },

        orderNumber: {
            id: 'orderNumber',
            label: 'Nr. Comanda',
            decorator: (row) => (
                <Pane display="flex" alignItems="center">
                    {withPreview && (
                        // Stack lifts the popover above the sidebar (.orchestra-menu is
                        // z-index 20; evergreen positions popovers at 10 by default), so a
                        // wide preview shifted to the left viewport edge stays visible.
                        <Stack value={21}>
                            {() => (
                                <Popover content={<OrderPreview items={row.items} />}>
                                    <Icon icon={EyeOpenIcon} size={16} marginRight={10} cursor="pointer" />
                                </Popover>
                            )}
                        </Stack>
                    )}
                    <Link to={`/orders/${row.id}`} target="_blank" rel="noopener noreferrer">
                        {row.orderNumber}
                    </Link>
                    <Pane marginLeft={8} display="inline">
                        <ReviewBlock {...row} />
                    </Pane>
                </Pane>
            ),
        },

        tags: {
            id: 'isPreorder',
            label: 'Tags',
            decorator: (row) => (
                <Pane display="flex" alignItems="center" gap={4} flexWrap="wrap">
                    <PreorderBlock {...row} />
                    <BackorderBlock {...row} />
                    <SinglesBlock {...row} />
                    <AlertBlock {...row} />
                    <StorePickupBlock {...row} />
                    <CompanyBlock {...row} />
                    <ExternalBlock {...row} />
                </Pane>
            ),
            options: [{ label: 'Tags', value: 1 }],
        },

        customer: {
            id: 'billingName',
            label: 'Nume',
            decorator: (row) => {
                const name = row.billingName || row.user?.name || row.deliveryName || 'Guest';
                const userId = row.userId || row.user?.id;
                return (
                    <Pane display="flex" alignItems="center">
                        {name}
                        {userId && (
                            <Icon
                                icon={UserIcon}
                                size={16}
                                cursor="pointer"
                                color="default"
                                marginLeft={8}
                                onClick={() => window.open(`/users/${userId}`, '_blank', 'noopener,noreferrer')}
                                title="View user account"
                            />
                        )}
                    </Pane>
                );
            },
        },

        status: {
            id: 'statusText',
            filterId: 'status',
            label: 'Status',
            decorator: (row) => (
                <StatusBlock status={row.status} statusText={row.statusText ?? labelOf(statusLabels, row.status)} />
            ),
            options: [
                { label: 'Toate', value: '' },
                { label: 'Plasata', value: 'placed' },
                { label: 'Confirmata', value: 'confirmed' },
                { label: 'Procesata', value: 'processed' },
                { label: 'Livrata', value: 'delivered' },
                { label: 'Finalizata', value: 'completed' },
                { label: 'Anulata', value: 'cancelled' },
                { label: 'Returnata', value: 'returned' },
            ],
        },

        payment: {
            id: 'paymentStatusText',
            filterId: 'paymentStatus',
            label: 'Plata',
            decorator: (row) => (
                <PaymentBlock
                    paymentStatus={row.paymentStatus}
                    paymentType={row.paymentType}
                    paymentStatusText={row.paymentStatusText ?? labelOf(paymentLabels, row.paymentStatus)}
                />
            ),
            options: [
                { label: 'Neplatita', value: 'unpaid' },
                { label: 'Plata Partiala', value: 'partial_payment' },
                { label: 'Platita', value: 'paid' },
                { label: 'Plata Esuata', value: 'payment_failed' },
                { label: 'Returnata', value: 'refunded' },
            ],
        },

        type: {
            id: 'orderType',
            label: 'Tip Comanda',
            decorator: (row) => (
                <TypeBlock orderType={row.orderType} orderTypeText={row.orderTypeText ?? labelOf(typeLabels, row.orderType)} />
            ),
            options: [
                { label: 'Toate', value: '' },
                { label: 'Facebook', value: 'facebook' },
                { label: 'Glovo', value: 'glovo' },
                { label: 'Online', value: 'online' },
                { label: 'Fizic', value: 'physical' },
                { label: 'Telefonic', value: 'phone' },
                { label: 'Email', value: 'email' },
            ],
        },

        delivery: {
            id: 'delivery',
            label: 'Livrare',
            decorator: (row) => labelOf(deliveryLabels, row.delivery),
        },

        grandTotal: {
            id: 'grandTotal',
            label: 'Valoare',
            decorator: (row) => (row.grandTotal > 0 ? `${Number(row.grandTotal).toFixed(2)} RON` : '-'),
        },

        issuesCount: {
            id: 'issues',
            filterId: 'issues',
            label: 'Tickete',
            decorator: (row) => (row.issues > 0 ? <Badge color="red">{row.issues}</Badge> : '-'),
        },

        emailOrderCount: {
            id: 'emailOrderCount',
            label: 'Nr. Comenzi',
            decorator: (row) => {
                const isFirstOrder = row.emailOrderCount === 1;
                return (
                    <span style={{ color: isFirstOrder ? 'green' : 'inherit', fontWeight: isFirstOrder ? 'bold' : 'normal' }}>
                        {row.emailOrderCount}
                    </span>
                );
            },
        },

        email: {
            id: 'email',
            label: 'Email',
        },

        createdAt: {
            id: 'createdAt',
            label: 'Creat',
            decorator: (row) => datetimeCell(row.createdAt),
        },

        updatedAt: {
            id: 'updatedAt',
            label: 'Updated',
            decorator: (row) => datetimeCell(row.updatedAt),
        },
    };
}
